/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikHelpers } from 'formik';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import {
    AccountsClient,
    AccountDto,
    GetAccountValuesDto,
    FormStepStatusDto,
    ValidationProblemDetails,
} from '../../../api/web-api-client';
import { tokenRequest } from '../../../authentication/authConfig';
import { ErrorType, WizardFormStepValues, WizardStepProps } from '../../../components/forms/WizardForm/types';
import { DiscardProps } from '../../../components/forms/FormikForm/types';
import { setDashboardNotification } from '../../../storage/notification';
import { NotificationSeverity } from '../../../storage/types';
import { AccountContextState } from '../../../authentication/accountContext';
import { HttpStatusCode } from '../../../types';
import organisationSubmitValidation from './validation';

const loadAccountDetails = (accounts: AccountInfo[], instance: IPublicClientApplication, accountId: number) => async (
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        const client = new AccountsClient();
        const tokenResult = await instance.acquireTokenSilent({
            ...tokenRequest,
            account: accounts[0],
        });
        client.setAuthToken(tokenResult.accessToken);

        const businessDetailsStep = await client.getAccountDetailsByOrgId(accountId, abortSignal);

        const wizardStepValues: WizardFormStepValues<GetAccountValuesDto> = {
            stepValues: { ...businessDetailsStep.stepValues },
        };

        return wizardStepValues;
    }

    throw Error('There was an error retrieving your organisation details.');
};

const completeAccountDetails = (accounts: AccountInfo[], instance: IPublicClientApplication, accountContext: AccountContextState | null) => async (
    values: AccountDto,
    isDirty: boolean,
    _: FormikHelpers<AccountDto>,
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new AccountsClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            await client.completeAccountDetails(
                {
                    // formId: 0,
                    formStep: {
                        ...values,
                    },
                    // interim fix for address lookup update,
                    // as using touched/setFieldTouched/setFieldValue doesn't seem to trigger
                    // formik isDirty for the autosuggest unless you blur another control after selection before submission
                    // isDirty: true,
                },
                abortSignal,
            );
            await accountContext?.setCompleted();
            if (values.name !== undefined) await accountContext?.setOrganisationAndBranch(values.name, values.businessOrTradingName !== undefined ? values.businessOrTradingName : '', values.branchOrLocationName !== undefined ? values.branchOrLocationName : '');
            setDashboardNotification({
                message: 'Your organisation details have been successfully updated.',
                severity: NotificationSeverity.Success,
            });
        } catch (error) {
            const problemDetails = error as ValidationProblemDetails;
            if (problemDetails) {
                switch (problemDetails.status) {
                    case HttpStatusCode.PreconditionFailed:
                        setDashboardNotification({
                            message: 'This branch/location name already exists. Please enter a unique branch/location name.',
                            severity: NotificationSeverity.Error,
                        });
                        break;
                    default:
                        setDashboardNotification({
                            message: 'There was an error saving your organisation.',
                            severity: NotificationSeverity.Error,
                        });
                }
            }
        }
    } else {
        throw Error('There was an error saving your organisation details.');
    }
};

// const cancelSave = () => {
//     setDashboardNotification({
//         message: 'Your organisation and contact details have not been updated.',
//         severity: NotificationSeverity.Information,
//     });
// };

const discardChanges: DiscardProps = {
    cancelButtonTitle: 'Cancel',
    discardButtonTitle: 'Cancel',
    showCancelButton: true,
    locationOnDiscard: '/',
    // onDiscard: cancelSave,
};

const getRedirectionLocationOnError = (errorCode: number, errorType: ErrorType) => {
    if (errorCode === 412) {
        return '/';
    }

    return undefined;
};

const updateAccountProps = (
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    statuses: FormStepStatusDto[],
    accountContext: AccountContextState | null,
    accountId: number,
)
: WizardStepProps<GetAccountValuesDto> => ({
    initialValues: {
        id: '',
        abn: '',
        branchOrLocationName: '',
        isDefaultOrganisation: undefined,
        businessWebsiteAddress: '',
        businessEmailAddress: '',
        contact: {},
        streetAddress: {},
        postalAddressSameAsStreetAddress: undefined,
        postalAddress: {},
        timestamp: '',
    },
    stepStatuses: statuses,
    loadStepValues: loadAccountDetails(accounts, instance, accountId),
    location: '/',
    title: 'Organisation',
    hidingFields: {
        isDefaultOrganisation: (x) => x.isDefaultOrganisation === true,
        postalAddress: (x) => x.postalAddressSameAsStreetAddress === true,
        contact: {
            titleOther: (x) => x.contact?.title !== 'Other',
        },
    },
    validateHard: organisationSubmitValidation,
    onSaveAndNext: completeAccountDetails(accounts, instance, accountContext),
    bannerTitle: 'Manage organisation',
    discard: discardChanges,
    getRedirectionLocationOnError,
});

export default updateAccountProps;
